import React, { useRef, useEffect, useState } from "react";
import moment from "moment";
import domToImage from "dom-to-image";
import { Button, Card, makeStyles } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    maxWidth: "800px",
    margin: "0 auto",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(3),
  },
  printButton: {
    backgroundColor: theme.palette.primary.main,
    color: "white",
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  reportWrapper: {
    width: "100%",
    fontFamily: "Arial, sans-serif",
    "& @media print": {
      width: "80mm",
      margin: "0 5mm",
    },
  },
  headerText: {
    fontSize: "18px",
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: theme.spacing(1),
  },
  divider: {
    borderBottom: "2px dotted #000",
    margin: "12px 0",
  },
  dataText: {
    fontSize: "14px",
    lineHeight: "1.5",
    "& > div": {
      marginBottom: "4px",
    },
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: theme.spacing(2),
    "& th": {
      fontSize: "16px",
      padding: "8px 0",
      borderBottom: "1px solid #ddd",
    },
    "& td": {
      fontSize: "14px",
      padding: "8px 0",
    },
  },
  summaryText: {
    fontSize: "15px",
    "& > div": {
      margin: "8px 0",
    },
  },
  flexBetween: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    margin: "16px 0 8px 0",
  },
  footer: {
    textAlign: "center",
    fontWeight: "bold",
    fontSize: "14px",
    marginTop: theme.spacing(2),
  },
  // Add hidden styles for the print content
  //   hiddenPrintContent: {
  //     position: "fixed",
  //     left: "-9999px",
  //     top: 0,
  //     width: "556px", // Same width as in InvoiceTemplate
  //     padding: "20px 10px",
  //     lineHeight: "35px",
  //     backgroundColor: "white",
  //     border: "0px solid #FFFFFF",
  //   },
}));

const ThermalSalesReportIMIN = ({ userProfile, salesData, selectedDate, startDate, endDate }) => {
  const classes = useStyles();
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const printRef = useRef();

  useEffect(() => {
    // Filter invoices for selected date range or single date
    let filtered;
    
    if (startDate && endDate) {
      // Use date range filtering
      const start = moment(startDate).startOf('day');
      const end = moment(endDate).endOf('day');
      
      filtered = salesData.invoices.filter((invoice) => {
        const invoiceDate = moment(invoice.createdAt);
        return invoiceDate.isBetween(start, end, null, '[]'); // inclusive of both start and end
      });
    } else {
      // Fallback to single date filtering
      filtered = salesData.invoices.filter(
        (invoice) =>
          moment(invoice.createdAt).format("YYYY-MM-DD") ===
          moment(selectedDate).format("YYYY-MM-DD")
      );
    }
    
    console.log("filtered", filtered);
    setFilteredInvoices(filtered);
  }, [selectedDate, startDate, endDate, salesData.invoices]);

  // Calculate category-wise totals
  // const categoryTotals = filteredInvoices.reduce((acc, invoice) => {
  //   invoice.items.forEach((item) => {
  //     const category = item?.item?.category?.categoryName || "Uncategorized";
  //     console.log(category)
  //     acc[category] =
  //       (acc[category] || 0) + item.quantity * item?.item?.units?.sale?.price;
  //   });
  //   return acc;
  // }, {});

  const categoryTotals = filteredInvoices.reduce((acc, invoice) => {
    invoice.items.forEach((item) => {
      const category = item?.item?.category?.categoryName || "Uncategorized";
      // Only process if category is not "Uncategorized"
      if (category !== "Uncategorized") {
        acc[category] =
          (acc[category] || 0) +
          item.quantity * (item?.item?.units?.sale?.price || 0);
      }
    });
    return acc;
  }, {});

  // Calculate payment mode totals
  // const paymentTotals = filteredInvoices.reduce((acc, invoice) => {
  //   const mode = invoice.paymentMode || "Other";
  //   acc[mode] = (acc[mode] || 0) + invoice.total;
  //   return acc;
  // }, {});

  // Modified payment totals calculation
  const paymentTotals = filteredInvoices.reduce((acc, invoice) => {
    // Check if invoice has any uncategorized items
    const hasUncategorized = invoice.items.some(
      (item) => !item?.item?.category?.categoryName
    );

    // Only include payment if no uncategorized items
    if (!hasUncategorized) {
      const mode = invoice.paymentMode || "Other";
      acc[mode] = (acc[mode] || 0) + invoice.total;
    }
    return acc;
  }, {});

  // Calculate summary totals
  const totalSales = Object.values(categoryTotals).reduce((a, b) => a + b, 0);
  // const totalDiscount = filteredInvoices.reduce(
  //   (acc, inv) => acc + (inv.discount || 0),
  //   0
  // );

  // Modified totalDiscount calculation
  const totalDiscount = filteredInvoices.reduce((acc, invoice) => {
    const hasUncategorized = invoice.items.some(
      (item) => !item?.item?.category?.categoryName
    );
    return hasUncategorized ? acc : acc + (invoice.discount || 0);
  }, 0);
  // const totalVat = filteredInvoices.reduce(
  //   (acc, inv) => acc + (inv.vat || 0),
  //   0
  // );

  // Modified totalVat calculation
  const totalVat = filteredInvoices.reduce((acc, invoice) => {
    // Check if invoice has any uncategorized items
    const hasUncategorized = invoice.items.some(
      (item) => !item?.item?.category?.categoryName
    );
    // Only include VAT if no uncategorized items
    return hasUncategorized ? acc : acc + (invoice.vat || 0);
  }, 0);

  const handlePrintWithImin = () => {
    // Check if IMIN printer is available
    if (window.IminPrinter) {
      var IminPrintInstance = new window.IminPrinter();
      console.log("IminPrinter", IminPrintInstance);

      try {
        // Create a WebSocket connection
        IminPrintInstance.connect().then(async (isConnect) => {
          // The WebSocket status is linked
          if (isConnect) {
            IminPrintInstance.initPrinter();
            const status = await IminPrintInstance.getPrinterStatus();
            console.log("Printer status:", status);

            const node = printRef.current;

            if (node) {
              domToImage.toJpeg(node).then(function (dataUrl) {
                IminPrintInstance.printSingleBitmap(dataUrl);
                setTimeout(function () {
                  // After the delay, feed paper
                  IminPrintInstance.printAndFeedPaper(100);
                  // setTimeout(function(){
                  //   alert("Function is being called! ThermalSalesReportIMIN");
                  //   IminPrintInstance.opencashBox();
                  // }, 1000);
                  // Cut the paper after feeding
                  IminPrintInstance.partialCut();
                }, 3000);
              });
            }
          }
        });
      } catch (err) {
        alert("Error: IMIN ERROR: " + err.message);
      }
    } else {
      alert("IminPrinter is not available.");
    }
  };

  return (
    <Card className={classes.card}>
      <div className={classes.buttonContainer}>
        <Button
          variant="contained"
          onClick={handlePrintWithImin}
          className={classes.printButton}
        >
          Print Report
        </Button>
      </div>

      {/* Visible preview */}
      <div id="thermal-sales-report" className={classes.reportWrapper}>
        <div className={classes.headerText}>Daily Sales Report</div>
        <div className={classes.headerText}>{userProfile.businessName}</div>

        <div className={classes.divider} />

        <div className={classes.dataText}>
          <div>
            {startDate && endDate 
              ? `Date Range: ${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`
              : `Date: ${moment(selectedDate).format("DD/MM/YYYY")}`
            }
          </div>
          <div>Printed On: {moment().format("DD/MM/YYYY HH:mm:ss")}</div>
          <div>Total Orders: {filteredInvoices.length}</div>
        </div>

        <div className={classes.divider} />

        <table className={classes.table}>
          <thead>
            <tr>
              <th style={{ textAlign: "left" }}>Category</th>
              <th style={{ textAlign: "right" }}>Amount(₹)</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(categoryTotals).map(([category, amount]) => (
              <tr key={category}>
                <td style={{ textAlign: "left" }}>{category}</td>
                <td style={{ textAlign: "right" }}>{amount.toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className={classes.divider} />

        <div className={classes.summaryText}>
          <div className={classes.flexBetween}>
            <span>Total Sales</span>
            <span>₹{totalSales.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total Discount</span>
            <span>₹{totalDiscount.toFixed(2)}</span>
          </div>
          <div className={classes.flexBetween}>
            <span>Total VAT</span>
            <span>₹{totalVat.toFixed(2)}</span>
          </div>
        </div>

        <div className={classes.divider} />

        <div className={classes.flexBetween}>
          <span className={classes.sectionTitle}>Net Sales</span>
          <span className={classes.sectionTitle}>
            ₹{(totalSales - totalDiscount + totalVat).toFixed(2)}
          </span>
        </div>

        {/* <div className={classes.sectionTitle}>Payment Mode Summary</div>
        {Object.entries(paymentTotals).map(([mode, amount]) => (
          <div key={mode} className={classes.flexBetween}>
            <span>{mode}</span>
            <span>₹{amount.toFixed(2)}</span>
          </div>
        ))} */}

        <div className={classes.divider} />

        <div className={classes.footer}>*** End of Report ***</div>
      </div>

      {/* Hidden printable version optimized for IMIN thermal printer */}
      <div style={{ position: "absolute", left: "-9999px", top: "-9999px" }}>
        <div
          ref={printRef}
          style={{
            width: "556px",
            padding: "20px 10px",
            lineHeight: "35px",
            backgroundColor: "white",
            border: "0px solid #FFFFFF",
          }}
        >
          <div
            style={{
              textAlign: "center",
              fontSize: "30px",
              fontWeight: "bold",
            }}
          >
            Daily Sales Report
          </div>
          <div
            style={{
              textAlign: "center",
              fontSize: "28px",
              fontWeight: "bold",
            }}
          >
            {userProfile.businessName}
          </div>

          <div style={{ fontSize: "22px", textAlign: "center", width: "100%" }}>
            {startDate && endDate 
              ? `Date Range: ${moment(startDate).format("DD/MM/YYYY")} - ${moment(endDate).format("DD/MM/YYYY")}`
              : `Date: ${moment(selectedDate).format("DD/MM/YYYY")}`
            }
          </div>
          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "10px",
            }}
          ></div>

          <div style={{ fontSize: "24px", marginTop: "10px" }}>
            <div>Printed: {moment().format("DD/MM/YYYY HH:mm")}</div>
            <div>Total Orders: {filteredInvoices.length}</div>
          </div>

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "10px",
            }}
          ></div>

          <div
            style={{ fontSize: "28px", fontWeight: "bold", marginTop: "15px" }}
          >
            Category Sales
          </div>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: "24px",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th style={{ textAlign: "left", fontSize: "26px" }}>
                  Category
                </th>
                <th style={{ textAlign: "right", fontSize: "26px" }}>
                  Amount(₹)
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(categoryTotals).map(([category, amount]) => (
                <tr key={category}>
                  <td style={{ textAlign: "left", fontSize: "24px" }}>
                    {category}
                  </td>
                  <td style={{ textAlign: "right", fontSize: "24px" }}>
                    {amount.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "15px",
            }}
          ></div>

          <div style={{ fontSize: "26px", marginTop: "15px" }}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span>Total Sales</span>
              <span>₹{totalSales.toFixed(2)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span>Total Discount</span>
              <span>₹{totalDiscount.toFixed(2)}</span>
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: "10px",
              }}
            >
              <span>Total VAT</span>
              <span>₹{totalVat.toFixed(2)}</span>
            </div>
          </div>

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "10px",
            }}
          ></div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: "28px",
              fontWeight: "bold",
              marginTop: "15px",
              marginBottom: "15px",
            }}
          >
            <span>Net Sales</span>
            <span>₹{(totalSales - totalDiscount + totalVat).toFixed(2)}</span>
          </div>

          <div
            style={{
              fontSize: "28px",
              fontWeight: "bold",
              marginTop: "10px",
            }}
          >
            Payment Mode Summary
          </div>
          {Object.entries(paymentTotals).map(([mode, amount]) => (
            <div
              key={mode}
              style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "24px",
                marginTop: "5px",
              }}
            >
              <span>{mode}</span>
              <span>₹{amount.toFixed(2)}</span>
            </div>
          ))}

          <div
            style={{
              width: "100%",
              borderBottom: "medium dotted #333333",
              height: "5px",
              marginTop: "20px",
            }}
          ></div>

          <div
            style={{
              fontSize: "26px",
              textAlign: "center",
              fontWeight: "bold",
              marginTop: "20px",
            }}
          >
            *** End of Report ***
          </div>
          <div
            style={{
              fontSize: "24px",
              paddingTop: "20px",
              width: "100%",
              textAlign: "center",
              fontStyle: "italic",
              fontWeight: "bold",
            }}
          >
            Powered By : Elixir Technologies Pvt. Ltd.
          </div>
        </div>
      </div>
    </Card>
  );
};

export default ThermalSalesReportIMIN;
